import { ApplicableCouponsRequest, ApplicableCouponsResponse } from '@/src/types/applicableCoupons'
import {
    AircraftAmenitiesRequest,
    AircraftAmenitiesResponse,
    FullBaggageResponse,
    MiniRuleData,
    SRPBaggageRequest,
    SRPMiniRuleRequest,
    TransitVisaRequest,
    TransitVisaResponse
} from '@/src/types/flight'
import { getCurrentLocale } from '@/src/utils/rtkQuery/getCurrentLocale'
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'

const baseQuery = fetchBaseQuery({
    prepareHeaders: headers => {
        const locale = getCurrentLocale()
        headers.set('Accept', 'application/json')
        headers.set('Content-Type', 'application/json')
        headers.set('X-CT-SOURCETYPE', 'web')
        headers.set('preferred-language', locale)
        return headers
    }
})

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 0 })

const SRPFlightDetailsApi = createApi({
    reducerPath: 'SRPFlightDetailsApi',
    baseQuery: baseQueryWithRetry,
    tagTypes: ['BaggageInfo', 'MiniRuleInfo'],
    endpoints: builder => ({
        getBaggageInfo: builder.query<FullBaggageResponse, SRPBaggageRequest>({
            query: body => ({
                url: `/flight/search/v2/freeBaggageInfo`,
                method: 'POST',
                body
            }),
            providesTags: (result, error, arg) => [{ type: 'BaggageInfo', id: arg?.sol[0]?.fre?.fk }]
        }),

        getMiniRuleInfo: builder.query<MiniRuleData[], { payload: SRPMiniRuleRequest[]; isSplrt: boolean }>({
            query: body => ({
                url: `/flight/search/v2/minirule`,
                //url: `/flights/getminirule?domain=www.flyin.com`, // for development as domain will come as localhost
                method: 'POST',
                body
            }),
            providesTags: (result, error, arg) => [{ type: 'MiniRuleInfo', id: arg.payload[0].os?.[0]?.fareKey }]
        }),
        getApplicableCoupons: builder.query<ApplicableCouponsResponse, ApplicableCouponsRequest>({
            query: body => ({
                url: `/flight/search/v2/getSrpCoupons`,
                method: 'POST',
                body
            })
        }),
        // One HTTP call per journey side (OW → 1, RT → 2, multicity → one per trip). The
        // per-side responses are merged into a single flagged-segment set for the card.
        getTransitVisaInfo: builder.query<TransitVisaResponse, TransitVisaRequest[]>({
            async queryFn (requests, _api, _extraOptions, fetchWithBQ) {
                const results = await Promise.all(
                    requests.map(body =>
                        fetchWithBQ({ url: `/flight/search/v2/transit-visa`, method: 'POST', body })
                    )
                )
                const failed = results.find(result => result.error)
                if (failed?.error) return { error: failed.error }

                const merged = results.reduce<TransitVisaResponse>(
                    (acc, result) => {
                        const data = result.data as TransitVisaResponse | undefined
                        if (data?.tv && data.tv !== 'N') acc.tv = data.tv
                        if (data?.owTransitVisaSegments?.length) acc.owTransitVisaSegments.push(...data.owTransitVisaSegments)
                        if (data?.rtTransitVisaSegments?.length) acc.rtTransitVisaSegments.push(...data.rtTransitVisaSegments)
                        return acc
                    },
                    { tv: 'N', owTransitVisaSegments: [], rtTransitVisaSegments: [] }
                )
                return { data: merged }
            }
        }),
        getAircraftAmenities: builder.query<AircraftAmenitiesResponse, AircraftAmenitiesRequest>({
            query: ({ airlineCode, aircraftCode, lang }) => ({
                url: `/bff/orbit/v1/itin/aircraft-amenities/${airlineCode}?aircraftCode=${aircraftCode}&lang=${lang}`,
                method: 'GET'
            })
        })
    })
})

export const {
    useGetBaggageInfoQuery,
    useGetMiniRuleInfoQuery,
    useGetApplicableCouponsQuery,
    useGetAircraftAmenitiesQuery,
    useGetTransitVisaInfoQuery
} = SRPFlightDetailsApi
export default SRPFlightDetailsApi
